import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft2,
  ArrowRight2,
  Cross,
} from "../CustomComponents/SwiftIcon/Icons";
import { numberFormat, priceFormat } from "../../exports/UtilFunctions";
import MFSearch from "./MFSearch";
import ServerRequest from "../../exports/ServerRequest";
import { generatePurchaseDropdownOptions } from "../../exports/generatePurchaseDropdownOptions";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import ConfirmationBox from "./ConfirmationBox";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import Pulse from "../Loader/Pulse";
import { MFBuySellErrorhandling } from "./MFErrorHandling";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import ConfirmationTable from "./ConfirmationTable";

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

const DEVICE_SIZE = getDeviceSize();

const MFBuySell = ({ popupData, portfolioData = [], setPopup, customer }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Fetching your order");
  const [opacityMsg, setOpacityMsg] = useState(true);
  const [confirmloading, setConfirmLoading] = useState(false);
  const [transferData, setTransferData] = useState([]);
  const [vpa, setVpa] = useState("");
  const [totalCurrentValue, setTotalCurrentValue] = useState(0);
  const [totalTransactionValue, setTotalTransactionValue] = useState(0);
  const [totalPostTransactionValue, setTotalPostTransactionValue] = useState(0);
  const [currentStep, setCurrentStep] = useState("Process");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmationMessage, setConfirmmationMessage] =
    useState("Placing your order");
  const [purchaseMessage, setPurchaseMessage] = useState("");
  const [clientAccounts, setClientAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedTxnType, setSelectedTxnType] = useState("NETBANKING");
  const [successData, setSuccessData] = useState([]);
  const [failedData, setFailedData] = useState([]);

  const isSmallDevice = DEVICE_SIZE === "S";

  const fetchedNAVs = useRef(new Set());
  const fetchedISINs = useRef(new Set());

  useEffect(() => {
    const loadData = () => {
      if (!popupData) return;

      const newItems = popupData.isSingle
        ? [
            {
              ...popupData,
              source: "popup",
              uniqueId: Date.now() + Math.random(),
            },
          ]
        : Array.isArray(popupData)
          ? popupData.map((item) => ({
              ...item,
              source: "popup",
              uniqueId: Date.now() + Math.random(),
            }))
          : [];

      setTransferData(newItems);
    };

    loadData();
  }, [popupData]);

  useEffect(() => {
    transferData.forEach((item) => {
      const isin = item.isin || item.ISIN;
      if (isin && !fetchedISINs.current.has(isin)) {
        fetchedISINs.current.add(isin); // Mark as fetched
        fetchPurchaseInfo(isin);
      }
    });
  }, [transferData]);

  useEffect(() => {
    if (!loading) return;

    const loadingMessages = ["Loading mutual fund data...", "Almost there..."];
    let index = 0;
    setLoadingMsg(loadingMessages[index]);
    setOpacityMsg(true);

    const loadingMessageInterval = setInterval(() => {
      setOpacityMsg(false);
      setTimeout(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingMsg(loadingMessages[index]);
        setOpacityMsg(true);
      }, 200);
    }, 3000);

    return () => clearInterval(loadingMessageInterval);
  }, [loading]);

  useEffect(() => {
    if (!confirmloading) return;

    const loadingMessages = ["Loading your transaction...", "Almost there..."];
    let index = 0;
    setConfirmmationMessage(loadingMessages[index]);
    setOpacityMsg(true);

    const loadingMessageInterval = setInterval(() => {
      setOpacityMsg(false);
      setTimeout(() => {
        index = (index + 1) % loadingMessages.length;
        setConfirmmationMessage(loadingMessages[index]);
        setOpacityMsg(true);
      }, 200);
    }, 3000);

    return () => clearInterval(loadingMessageInterval);
  }, [loading]);

  useEffect(() => {
    if (popupData?.isNewFund) {
      setLoading(false);
    } else if (Array.isArray(transferData)) {
      const allDataFetched =
        transferData.length > 0 &&
        transferData.every(
          (item) => item.purchaseInfo && item.current_nav !== undefined,
        );
      setLoading(!allDataFetched);
    } else {
      setLoading(true);
    }
  }, [transferData]);

  useEffect(() => {
    transferData.forEach((item) => {
      const isin = item.isin || item.ISIN;
      if (
        isin &&
        (item.current_nav === undefined || item.current_nav === null) &&
        !fetchedNAVs.current.has(isin)
      ) {
        fetchedNAVs.current.add(isin);
        fetchCurrentNAV(isin);
      }
    });
  }, [transferData]);

  useEffect(() => {
    let investmentValue = 0;
    let transaction = 0;
    let postTransaction = 0;

    transferData.forEach((val) => {
      investmentValue += Number(val.investment_value) || 0;
      transaction += Number(val.selectedAmount) || 0;
      postTransaction += Number(val.post_transaction) || 0;
    });

    setTotalCurrentValue(investmentValue.toFixed(2));
    setTotalTransactionValue(transaction);
    setTotalPostTransactionValue(postTransaction);
  }, [transferData]);

  useEffect(() => {
    console.log(selectedBank);
  }, [selectedBank]);

  const fetchPurchaseInfo = async (isin) => {
    try {
      const res = await ServerRequest({
        url: `/mf/purchase/${isin}`,
        method: "GET",
      });

      if (res) {
        setTransferData((prev) =>
          prev.map((item) =>
            (item.isin || item.ISIN) === isin
              ? { ...item, purchaseInfo: res.data }
              : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching purchase info for ISIN:", isin, error);
    }
  };

  const fetchCurrentNAV = async (isin) => {
    try {
      const res = await ServerRequest({
        url: `/mf/latestnav/${isin}`,
        method: "get",
      });
      const nav = res?.result?.latest_nav;

      if (res) {
        setTransferData((prev) =>
          prev.map((item) =>
            (item.isin || item.ISIN) === isin
              ? { ...item, current_nav: nav }
              : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching current NAV for ISIN:", isin, error);
    }
  };

  const handleProcess = async () => {
    try {
      const res = await ServerRequest({
        url: `/nse/get-client-account?account_code=${customer.account_code}`,
        method: "get",
      });

      console.log("selct banck transferdata", transferData);

      if (res?.data?.report_data?.length) {
        setClientAccounts(res.data.report_data);
        setSelectedBank(res.data.report_data[0].account_no_1);
        setCurrentStep("Confirmation");
      }
    } catch (error) {
      console.log("Error fetching client account", error);
    }
  };

  const account = clientAccounts[0] || {};
  const banks = [];

  for (let i = 1; i <= 5; i++) {
    const bankName = account[`bank_name_${i}`]?.trim();
    const defaultFlag = account[`default_bank_flag_${i}`]?.trim();

    if (bankName) {
      banks.push({
        bankName,
        defaultFlag,
        accountNo: account[`account_no_${i}`],
      });
    }
  }

  const formattedOptions = banks.map((bank) => ({
    label: (
      <div className="bank-option">
        <div className="bank-name">{bank.bankName}</div>
        <div className="bank-account">{bank.accountNo}</div>
      </div>
    ),
    value: bank.accountNo, // or whole object if needed
  }));

  const defaultBank = clientAccounts.find((acc) => acc.default_flag === "Y");
  const defaultValue =
    defaultBank?.account_number ?? formattedOptions[0]?.value;

  const handleConfirmClick = () => {
    if (selectedTxnType === "UPI" && (!vpa || vpa.trim() === "")) {
      Alert({
        TitleText: "Warning",
        Message: "Please enter a valid UPI ID.",
        ...warning_config,
      });
      return; // stop here only for UPI without VPA
    }
    handleConfirm(); // proceed for all other cases
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);

    const transactionTemplate = {
      order_ref_number: "",
      scheme_code: "",
      trxn_type: "P",
      buy_sell_type: "",
      client_code: "",
      demat_physical: "P",
      order_amount: "",
      folio_no: "",
      remarks: "Order Remark",
      kyc_flag: "Y",
      sub_broker_code: "",
      euin_number: "",
      euin_declaration: "N",
      min_redemption_flag: "N",
      dpc_flag: "Y",
      all_units: "N",
      redemption_units: "",
      sub_broker_arn: "",
      bank_ref_no: "",
      account_no: "",
      mobile_no: "",
      email: "",
      mandate_id: "",
      filler1: "",
      trxn_order_id: "",
      trxn_status: "",
      trxn_remark: "",
    };

    try {
      const res = await ServerRequest({
        url: "/mf/orderreferenceno",
        method: "get",
      });
      const orderRefNumber = res.data;
      const newTranferdata = transferData.filter(
        (item) => parseFloat(item.selectedAmount) > 0,
      );
      setTransferData(newTranferdata);
      const transactionDetailsArray = await Promise.all(
        newTranferdata.map(async (item) => {
          const transactionObj = {
            ...transactionTemplate,
            order_ref_number: orderRefNumber,
            folio_no: item.folio_no || "",
            scheme_code:
              parseFloat(item.selectedAmount || 0) >
              parseFloat(item.purchaseInfo?.min_purchase_amount_l1)
                ? item.purchaseInfo?.scheme_code_l1
                : item.purchaseInfo?.scheme_code ||
                  item.scheme_code ||
                  item.Scheme_Code ||
                  "",
            buy_sell_type:
              !item.folio_no || item.folio_no.trim() === ""
                ? "FRESH"
                : "ADDITIONAL",
            client_code: clientAccounts[0]?.client_code || "",
            account_no: selectedBank || "",
            order_amount: String(item.selectedAmount ?? ""),
            email: clientAccounts.email,
            order_ref_number: orderRefNumber,
          };
          return transactionObj;
        }),
      );

      const res2 = await ServerRequest({
        url: "/nse/purchase-order-entry",
        method: "post",
        data: { transaction_details: transactionDetailsArray },
      });

      const results = res2.data.transaction_details || [];
      const success = results.filter((r) => r.trxn_status === "TRXN SUCCESS");
      const fail = results.filter((r) => r.trxn_status === "TRXN FAILED");

      console.log("success", success);

      setSuccessData(success);
      setFailedData(fail);

      console.log("newTranferdata", newTranferdata);

      // If we have success orders → ping get-purchase-payment
      if (success.length > 0) {
        try {
          const res3 = await ServerRequest({
            url: "/nse/get-purchase-payment",
            method: "post",
            data: {
              transferData: newTranferdata.filter((item) =>
                success.some(
                  (s) =>
                    s.scheme_code === item.purchaseInfo.scheme_code ||
                    s.scheme_code === item.purchaseInfo.scheme_code_l1,
                ),
              ),
              clientAccounts,
              transaction_details: success,
              selectedTxnType,
              vpa,
            },
          });

          if (res3?.data?.status?.toLowerCase() === "success") {
            setPurchaseMessage("Please check your email for transaction");
          }
        } catch (err) {
          console.error("get-purchase-payment failed:", err);
          setPurchaseMessage("Payment request failed, please try again later.");
        }
      }

      setCurrentStep("FinalConfirmation");
    } catch (error) {
      console.error("Failed to get order reference number:", error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const removeRow = (index) => {
    setTransferData((prev) => prev.filter((_, i) => i !== index));
  };

  const normalizeData = (data) => ({
    scheme_name: data.scheme_name || data.Scheme_Name || "",
    scheme_code: data.scheme_code || data.Scheme_Code || "",
    isin: data.isin || data.ISIN || "",
    folio_no: data.folio_no || "",
  });

  const handleClick = (symbol) => {
    if (!symbol) return;
    const normalized = normalizeData(symbol);
    const portfolioMatch = (portfolioData || []).find(
      (p) => (p.isin || p.ISIN) === normalized.isin,
    );
    const dataToPush = {
      ...(portfolioMatch || symbol),
      source: "search",
      uniqueId: Date.now() + Math.random(),
    };
    setTransferData((prev) => {
      const exists = prev.some(
        (item) =>
          (item.isin || item.ISIN) === (dataToPush.isin || dataToPush.ISIN),
      );
      return exists ? prev : [...prev, dataToPush];
    });
  };

  const handleAmountChange = (value, uniqueId) => {
    setTransferData((prev) =>
      prev.map((item) => {
        if (item.uniqueId === uniqueId && item.selectedAmount !== value) {
          const nav = parseFloat(item.current_nav || 0);
          const amount = parseFloat(value || 0);
          const investmentValue = parseFloat(item.investment_value || 0);

          const new_units = nav > 0 ? (amount / nav).toFixed(2) : "0.00";
          const post_transaction = (investmentValue + amount).toFixed(2);

          return {
            ...item,
            selectedAmount: value,
            new_units,
            post_transaction,
          };
        }
        return item;
      }),
    );
  };

  const getTransactionTypeOptions = (amount) => {
    if (amount < 100000) {
      return [
        { label: "UPI", value: "UPI" },
        { label: "Net Banking", value: "NETBANKING" },
        { label: "NEFT/ RTGS", value: "NEFT" },
      ];
    } else if (amount < 1000000) {
      return [
        { label: "Net Banking", value: "NETBANKING" },
        { label: "NEFT/ RTGS", value: "NEFT" },
      ];
    } else {
      return [{ label: "NEFT/ RTGS", value: "NEFT" }];
    }
  };

  const transactionTypeOptions = getTransactionTypeOptions(
    totalTransactionValue,
  );
  return (
    <>
      {loading || confirmloading ? (
        <div className="app__login__screen">
          <div className="app__login__process">
            <Pulse />
            {/* <p className={opacityMsg ? 'fadeIn' : 'fadeOut'}>{confirmloading ? confirmationMessage : loadingMsg}</p> */}
          </div>
        </div>
      ) : (
        <>
          <div className="swift-mf-buysell-popup-header">
            <div
              className={`swift-mf-popup-btn ${Array.isArray(popupData) ? popupData[0]?.type?.toLowerCase() : popupData?.type.toLowerCase() || ""}`}
            >
              {/* {Array.isArray(popupData) ? popupData[0]?.type : popupData?.type} */}
              Buy Transaction
            </div>

            {currentStep === "Process" &&
              (!popupData?.isSingle || popupData?.isNewFund) && (
                <MFSearch handleSelect={handleClick} />
              )}
          </div>

          <div className="swift-mf-popup-body">
            <div
              className={`mf-BuySell-container ${currentStep === "Confirmation" ? "Confirmation" : ""}`}
            >
              {currentStep === "Process" && (
                <>
                  {DEVICE_SIZE === "S" ? (
                    <>
                      <BuySellCardContainer
                        transferData={transferData}
                        removeRow={removeRow}
                        handleAmountChange={handleAmountChange}
                        CustomSelect={CustomSelect}
                      />
                      <BuySellConfig
                        current_value={totalCurrentValue}
                        transaction_value={totalTransactionValue}
                        postTransaction={totalPostTransactionValue}
                      />
                    </>
                  ) : (
                    <>
                      <table>
                        <thead>
                          <tr>
                            <td className="scheme-name">Scheme Name</td>
                            <td className="isin">Scheme Code</td>
                            <td className="isin">ISIN</td>
                            <td className="folio">Folio No</td>
                            <td className="inv-value">Current Value</td>
                            <td>Current Nav</td>
                            <td className="post-transaction">
                              Transaction Value
                            </td>
                            <td className="isin">Units</td>
                            <td className="post-transaction">
                              Post Transaction Value
                            </td>
                            <td className="cross"></td>
                          </tr>
                        </thead>
                        <tbody>
                          {transferData.map((h, idx) => {
                            if (!h.purchaseInfo) return null;

                            const dropdownOptions =
                              generatePurchaseDropdownOptions(
                                parseInt(
                                  h.purchaseInfo.min_purchase_amount,
                                  10,
                                ),
                                parseInt(h.purchaseInfo.multiple, 10),
                                parseInt(
                                  h.purchaseInfo.purchase_allowed_l1 === "Y"
                                    ? h.purchaseInfo.max_purchase_amount_l1
                                    : h.purchaseInfo.max_purchase_amount,
                                  10,
                                ),
                              ).map((val) => ({
                                label:
                                  val === "ALL"
                                    ? "All Units"
                                    : val === ""
                                      ? "-"
                                      : priceFormat(val),
                                value:
                                  val === "ALL"
                                    ? h.investment_value
                                    : val === ""
                                      ? 0
                                      : val,
                              }));

                            return (
                              <tr key={idx}>
                                <td className="body-scheme-name">
                                  {h.scheme_name || h.Scheme_Name}
                                </td>
                                <td className="body-category">
                                  {parseFloat(h.selectedAmount || 0) >
                                  parseFloat(
                                    h.purchaseInfo?.min_purchase_amount_l1,
                                  )
                                    ? h.purchaseInfo?.scheme_code_l1
                                    : h.purchaseInfo?.scheme_code ||
                                      h.scheme_code ||
                                      h.Scheme_Code ||
                                      ""}
                                </td>
                                <td>{h.isin || h.ISIN}</td>
                                <td>{h.folio_no || ""}</td>
                                <td>{priceFormat(h.investment_value || 0)}</td>
                                <td>{priceFormat(h.current_nav || 0)}</td>
                                <td className="custom-text-td">
                                  {h.purchaseInfo?.purchase_allowed === "Y" ? (
                                    <div className="bysell-custom-select-container">
                                      <CustomSelect
                                        value={h.selectedAmount}
                                        defaultIndex={
                                          h.selectedAmount
                                            ? dropdownOptions.findIndex(
                                                (opt) =>
                                                  opt.value ===
                                                  h.selectedAmount,
                                              )
                                            : -1
                                        }
                                        options={dropdownOptions.map(
                                          (opt) => opt.label,
                                        )}
                                        width={180}
                                        height={50}
                                        placeholder="-"
                                        onTypeChange={(selectedLabel) => {
                                          const matched = dropdownOptions.find(
                                            (opt) =>
                                              opt.label === selectedLabel,
                                          );
                                          handleAmountChange(
                                            matched?.value ?? 0,
                                            h.uniqueId,
                                          ); // use uniqueId here
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <span className="not_allowed">
                                      Not Allowed
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {h.new_units
                                    ? numberFormat(h.new_units)
                                    : "-"}
                                </td>
                                <td>
                                  {h.post_transaction
                                    ? priceFormat(h.post_transaction)
                                    : "-"}
                                </td>
                                {h.source === "search" && (
                                  <td
                                    onClick={() => removeRow(idx)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <Cross
                                      color={COLOR_VARS["SWIFT_COLOR4"]}
                                      size={30}
                                    />
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                          {transferData.some(
                            (val) => val.isSingle === false,
                          ) && (
                            <tr className="total-row">
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>{priceFormat(totalCurrentValue)}</td>
                              <td></td>
                              <td>{priceFormat(totalTransactionValue)}</td>
                              <td></td>
                              <td>{priceFormat(totalPostTransactionValue)}</td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
              {currentStep === "Confirmation" && (
                <div className="mf_buysell_bank_selection_container">
                  <div style={{ padding: "10px 0" }}>
                    <p className="mf_buysell_bank_selection">Bank Details</p>
                    <div style={{ marginLeft: "10px" }}>
                      <CustomSelect
                        options={formattedOptions.map((opt) => opt.label)}
                        title={"Select Bank Details"}
                        defaultIndex={
                          selectedBank
                            ? formattedOptions.findIndex(
                                (opt) => opt.value === selectedBank,
                              )
                            : -1
                        }
                        value={
                          formattedOptions.find(
                            (opt) => opt.value === selectedBank,
                          )?.label ||
                          formattedOptions.find(
                            (opt) => opt.value === defaultValue,
                          )?.label
                        }
                        onTypeChange={(label) => {
                          const selected = formattedOptions.find(
                            (opt) => opt.label === label,
                          );
                          setSelectedBank(selected?.value);
                        }}
                        width={200}
                        height={40}
                      />
                    </div>
                  </div>
                  <div style={{ padding: "10px 0" }}>
                    <p className="mf_buysell_bank_selection">
                      Transaction Mode
                    </p>
                    {selectedBank && (
                      <div style={{ marginLeft: "10px" }}>
                        <CustomSelect
                          title={"Select Payment Mode"}
                          options={transactionTypeOptions.map(
                            (opt) => opt.label,
                          )}
                          defaultIndex={
                            selectedTxnType
                              ? transactionTypeOptions.findIndex(
                                  (opt) => opt.value === selectedTxnType,
                                )
                              : transactionTypeOptions.findIndex(
                                  (opt) => opt.value === "NETBANKING",
                                )
                          }
                          value={selectedTxnType}
                          onTypeChange={(selectedLabel) => {
                            const selected = transactionTypeOptions.find(
                              (opt) => opt.label === selectedLabel,
                            );
                            setSelectedTxnType(selected?.value);
                            console.log(
                              "Selected Payment Mode:",
                              selected?.value,
                            );
                          }}
                          width={200}
                          height={40}
                        />
                      </div>
                    )}
                  </div>

                  {selectedTxnType === "UPI" && (
                    <div style={{ padding: "10px" }}>
                      <p className="mf_buysell_bank_selection">UPI ID</p>
                      <div
                        className="mf-upi-input"
                        style={{ marginLeft: "10px" }}
                      >
                        <input
                          type="text"
                          placeholder="e.g. 1234567890@ybl"
                          onChange={(e) => {
                            setVpa(e.target.value);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <>
                {currentStep === "FinalConfirmation" && (
                  <>
                    {successData.length > 0 || failedData.length > 0 ? (
                      <ConfirmationTable
                        successData={successData}
                        failedData={failedData}
                        purchaseMessage={purchaseMessage}
                        transferData={transferData}
                      />
                    ) : (
                      <ConfirmationBox
                        msg={
                          confirmMessage === "Success"
                            ? "Your order has been successfully confirmed"
                            : "Looks like your transaction/order has failed."
                        }
                        addMsg={purchaseMessage}
                        confirmationType={confirmMessage}
                      />
                    )}
                  </>
                )}
              </>
            </div>

            {currentStep !== "FinalConfirmation" && (
              <div className="step-button mf-swift-buysell-confirm-button">
                {currentStep === "Process" && (
                  <>
                    <button
                      className="btn-outline"
                      onClick={() => {
                        setPopup(false);
                      }}
                    >
                      Back
                    </button>
                    <CustomIconButton
                      title="Select bank"
                      onClick={handleProcess}
                      disabled={
                        !transferData.some(
                          (item) => Number(item.selectedAmount) > 0,
                        )
                      }
                      icon={null}
                    />
                  </>
                )}
                {currentStep === "Confirmation" && (
                  <>
                    <CustomIconButton
                      title="Back"
                      onClick={() => setCurrentStep("Process")}
                      icon={
                        <ArrowLeft2
                          size={20}
                          color={COLOR_VARS["SWIFT_COLOR6"]}
                        />
                      }
                    />
                    <CustomIconButton
                      title={`${currentStep}`}
                      onClick={handleConfirmClick}
                      icon={null}
                    />
                  </>
                )}
              </div>
            )}
            {currentStep === "FinalConfirmation" && (
              <div className="step-button final-close mf-swift-buysell-confirm-button">
                <CustomIconButton
                  title={`Close`}
                  onClick={() => setPopup(false)}
                  icon={<Cross size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

const BuySellCardContainer = ({
  transferData,
  removeRow,
  handleAmountChange,
  CustomSelect,
}) => {
  return (
    <>
      {transferData.map((h, idx) => {
        const dropdownOptions = generatePurchaseDropdownOptions(
          parseInt(h.purchaseInfo?.min_purchase_amount ?? 0, 10),
          parseInt(h.purchaseInfo?.multiple ?? 0, 10),
          parseInt(
            h.purchaseInfo?.purchase_allowed_l1 === "Y"
              ? h.purchaseInfo?.max_purchase_amount_l1
              : h.purchaseInfo?.max_purchase_amount,
            10,
          ),
        ).map((val) => ({
          label:
            val === "ALL" ? "All Units" : val === "" ? "-" : priceFormat(val),
          value: val === "ALL" ? h.investment_value : val === "" ? 0 : val,
        }));

        return (
          <>
            <div className="mf-watchlist-card">
              <div className="mfwc-info">
                <div className="isin-price">
                  <p className="isin-type">{h.isin || h.ISIN}</p>
                  <p className="price-change">
                    {priceFormat(h.current_nav || 0)}
                  </p>
                </div>
                <p className="name">{h.scheme_name || h.Scheme_Name}</p>
                <p className="category">
                  {parseFloat(h.selectedAmount || 0) >
                  parseFloat(h.purchaseInfo?.min_purchase_amount_l1)
                    ? h.purchaseInfo?.scheme_code_l1
                    : h.purchaseInfo?.scheme_code ||
                      h.scheme_code ||
                      h.Scheme_Code ||
                      ""}
                </p>
              </div>
              <div className="mfwc-config">
                <div className="mfwc-config-row">
                  <p>
                    Investment Value
                    <span>{priceFormat(h.investment_value || 0)}</span>
                  </p>
                  <p>
                    Transaction Value
                    {h.purchaseInfo?.purchase_allowed === "Y" ? (
                      <div className="bysell-custom-select-container">
                        <CustomSelect
                          value={h.selectedAmount}
                          defaultIndex={
                            h.selectedAmount
                              ? dropdownOptions.findIndex(
                                  (opt) => opt.value === h.selectedAmount,
                                )
                              : -1
                          }
                          options={dropdownOptions.map((opt) => opt.label)}
                          width={180}
                          height={50}
                          placeholder="-"
                          onTypeChange={(selectedLabel) => {
                            const matched = dropdownOptions.find(
                              (opt) => opt.label === selectedLabel,
                            );
                            handleAmountChange(matched?.value ?? 0, h.uniqueId); // use uniqueId here
                          }}
                        />
                      </div>
                    ) : (
                      <span className="not_allowed">Not Allowed</span>
                    )}
                  </p>
                </div>
                <div className="mfwc-config-row">
                  <p>
                    Units
                    <span>{h.new_units ? numberFormat(h.new_units) : "-"}</span>
                  </p>
                  <p>
                    Post Transaction Value
                    <span>
                      {h.post_transaction
                        ? priceFormat(h.post_transaction)
                        : "-"}
                    </span>
                  </p>
                </div>
                <div className="mfwc-config-row">
                  <p></p>
                  <p>
                    {h.source === "search" && (
                      <span
                        onClick={() => removeRow(idx)}
                        style={{ cursor: "pointer" }}
                      >
                        <p>Remove</p>
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </>
        );
      })}
    </>
  );
};

function BuySellConfig({ current_value, transaction_value, postTransaction }) {
  return (
    <div className="portfolio-config">
      <div className="portfolio-rows">
        <div className="portfolio-info-box">
          <span>Current Value</span>
          <p>Rs. {numberFormat(current_value)}</p>
        </div>
        <div className="portfolio-info-box">
          <span>Transaction Value</span>
          <p>Rs. {numberFormat(transaction_value)}</p>
        </div>
        <div className="portfolio-info-box">
          <span>Post Transaction</span>
          <p>Rs. {numberFormat(postTransaction)}</p>
        </div>
      </div>
    </div>
  );
}

export default MFBuySell;
